import { Injectable } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { DepositDetailsType, TermDepositAccountDetailsType } from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap } from 'rxjs';
import { GetDepositAccountInfo, SetDepositAccountInfo } from '../actions/deposit-account-info.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import { AccountService } from '../services/account-service/account-service.service';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';

export interface DepositAccountInfoStateModel {
  depositAmount?: number;
  fee?: number;
  feeRate?: number;
  currency?: string;
  totalUnit?: number;
  currentExchangeRate?: number;
  initialExchangeRate?: number;
  lastInterestCredit?: {
    date: string;
    amount: number;
    currency: string;
  };
  totalBalance?: {
    currency: string;
    amount: number;
  };
  totalInterest?: {
    currency: string;
    amount: number;
    incrementPercentage: number;
  };
  totalBalanceWithInterest?: {
    currency: string;
    amount: number;
  };
  initialFundAmount?: number;
  pendingInitialFundAmount?: number;
  fundGrowthPercentage?: number;
  fundGrowthValue?: number;
  currentExchangeRateCreatedDate?: string;
  isLoading: boolean;
}

@State<DepositAccountInfoStateModel>({
  name: 'depositAccountInfo',
  defaults: {
    isLoading: false,
  },
})
@Injectable()
export class DepositAccountInfoState {
  constructor(private accountService: AccountService, private alertHandlerService: AlertHandlerService) {}
  @Selector()
  static getDepositAccountInfo(state: DepositAccountInfoStateModel) {
    return state;
  }

  @Action(SetDepositAccountInfo)
  setDepositAccountInfo(ctx: StateContext<DepositAccountInfoStateModel>, action: SetDepositAccountInfo) {
    ctx.setState(action.state);
  }

  @Action(GetDepositAccountInfo)
  fetchDepositAccountInfo(ctx: StateContext<DepositAccountInfoStateModel>, action: GetDepositAccountInfo) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetDepositAccountInfo' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getDepositAccountInfo, true));

    const params = action.type;

    return this.accountService.getDepositAccountInfo(params).pipe(
      tap((accountsResult: TermDepositAccountDetailsType) => {
        // Create a state update object based on the requested types
        const stateUpdate: Partial<DepositAccountInfoStateModel> = {
          isLoading: false,
        };

        // Check if BALANCE type was requested
        if (params.includes(DepositDetailsType.BALANCE)) {
          stateUpdate.totalBalance = accountsResult.totalBalance;
        }

        // Check if INTEREST type was requested
        if (params.includes(DepositDetailsType.INTEREST)) {
          stateUpdate.totalInterest = accountsResult.totalInterest;
        }

        if (params.includes(DepositDetailsType.BALANCE_WITH_INTEREST)) {
          stateUpdate.totalBalanceWithInterest = accountsResult.totalBalanceWithInterest;
        }

        // Check if DEPOSIT_DETAILS type was requested
        if (params.includes(DepositDetailsType.DEPOSIT_DETAILS)) {
          stateUpdate.depositAmount = accountsResult.depositAmount;
          stateUpdate.fee = accountsResult.fee;
          stateUpdate.feeRate = accountsResult.feeRate;
          stateUpdate.currency = accountsResult.currency;
          stateUpdate.totalUnit = accountsResult.totalUnit;
          stateUpdate.currentExchangeRate = accountsResult.currentExchangeRate;
          stateUpdate.currentExchangeRateCreatedDate = accountsResult.currentExchangeRateCreatedDate;
          stateUpdate.initialExchangeRate = accountsResult.initialExchangeRate;
          stateUpdate.lastInterestCredit = accountsResult.lastInterestCredit;
          stateUpdate.initialFundAmount = accountsResult.initialFundAmount;
          stateUpdate.pendingInitialFundAmount = accountsResult.pendingInitialFundAmount;
          stateUpdate.fundGrowthPercentage = accountsResult.fundGrowthPercentage;
          stateUpdate.fundGrowthValue = accountsResult.fundGrowthValue;
          stateUpdate.totalBalance = accountsResult.totalBalance;
        }

        // Update the state with only the relevant fields
        ctx.patchState({
          ...ctx.getState(),
          ...stateUpdate,
        });

        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getDepositAccountInfo, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetDepositAccountInfo' }));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getDepositAccountInfo, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetDepositAccountInfo' }));
        throw _err;
      }),
    );
  }
}
